import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import { Target, Loader2, AlertTriangle } from "lucide-react";

// Visual language per opportunity type. Striking distance and CTR gap are the
// cheap wins, so they get the strongest colors.
const TYPES = {
  striking_distance: { label: "Striking distance", color: "#002FA7", bg: "#002FA71A" },
  ctr_gap: { label: "CTR gap", color: "#B45309", bg: "#FFD60033" },
  page_one_push: { label: "Page 1 push", color: "#047857", bg: "#00C8531A" },
  performing: { label: "Performing", color: "#6B7280", bg: "#6B72801A" },
  long_shot: { label: "Long shot", color: "#9CA3AF", bg: "#9CA3AF1A" },
  unknown: { label: "Unknown", color: "#9CA3AF", bg: "#9CA3AF1A" },
};

function TypeBadge({ type }) {
  const t = TYPES[type] || TYPES.unknown;
  return (
    <span
      className="inline-block px-2 py-0.5 rounded-full text-xs font-bold whitespace-nowrap"
      style={{ backgroundColor: t.bg, color: t.color }}
    >
      {t.label}
    </span>
  );
}

function fmtInt(v) {
  return (v ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
}

export default function KeywordOpportunities({ connId, site, days }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");

  // Each run costs a DataForSEO lookup, so reset (rather than auto-refetch)
  // whenever the site or date range changes — the user re-runs deliberately.
  useEffect(() => {
    setData(null);
    setFilter("all");
  }, [connId, site, days]);

  const run = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/gsc/connections/${connId}/opportunities`, {
        params: { site, days },
      });
      setData(d);
      if (d.volume_error) {
        toast.warning("Search volume unavailable", { description: d.volume_error });
      }
    } catch (e) {
      toast.error("Could not load opportunities", {
        description: formatApiErrorDetail(e.response?.data?.detail) || e.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const rows = (data?.opportunities || []).filter(
    (o) => filter === "all" || o.type === filter
  );

  const availableTypes = Object.keys(data?.counts || {}).filter((t) => TYPES[t]);

  return (
    <div className="bg-white border border-gray-200 shadow-sm rounded-xl p-6 animate-fade-up" data-testid="keyword-opportunities">
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <div className="flex items-center gap-2">
          <Target className="w-5 h-5 text-klein" />
          <h3 className="font-heading text-xl font-bold tracking-tight">Keyword Opportunities</h3>
        </div>
        {data && (
          <Button variant="outline" onClick={run} disabled={loading} className="border-gray-300" data-testid="opportunities-refresh">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Re-run"}
          </Button>
        )}
      </div>

      {!data ? (
        <div className="text-center py-8">
          <p className="text-gray-500 max-w-lg mx-auto text-sm">
            Cross-reference your Search Console queries with real search volume to find the cheapest
            available traffic — keywords stuck on page 2, and pages that rank well but under-earn clicks.
          </p>
          <Button onClick={run} disabled={loading} className="mt-5 bg-klein hover:bg-blue-800 text-white" data-testid="opportunities-run">
            {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyzing…</> : "Find opportunities"}
          </Button>
        </div>
      ) : (
        <>
          {data.volume_error && (
            <div className="flex items-start gap-2 bg-warn/10 border border-yellow-300 rounded-lg p-3 mb-4 text-sm text-yellow-800">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{data.volume_error}</span>
            </div>
          )}

          {/* Type filters */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <button
              onClick={() => setFilter("all")}
              className={`px-3 py-1 rounded-full text-xs font-bold border transition-colors ${
                filter === "all" ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-gray-300 text-gray-600 hover:bg-gray-50"
              }`}
              data-testid="opp-filter-all"
            >
              All {data.opportunities?.length || 0}
            </button>
            {availableTypes.map((t) => (
              <button
                key={t}
                onClick={() => setFilter(t)}
                className={`px-3 py-1 rounded-full text-xs font-bold border transition-colors ${
                  filter === t ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-gray-300 text-gray-600 hover:bg-gray-50"
                }`}
                data-testid={`opp-filter-${t}`}
              >
                {TYPES[t].label} {data.counts[t]}
              </button>
            ))}
          </div>

          <div className="text-xs text-gray-400 mb-3">
            {data.total_queries} queries analyzed · {data.start_date} → {data.end_date}
          </div>

          {rows.length === 0 ? (
            <div className="p-8 text-center text-gray-500 text-sm">No opportunities in this category.</div>
          ) : (
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <div className="max-h-[520px] overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="pl-4">Keyword</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-right">Pos.</TableHead>
                      <TableHead className="text-right">Impr.</TableHead>
                      <TableHead className="text-right">Volume</TableHead>
                      <TableHead className="text-right">Potential</TableHead>
                      <TableHead className="text-right pr-4">Score</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((o, i) => (
                      <TableRow key={i} data-testid={`opp-row-${i}`}>
                        <TableCell className="pl-4 font-medium max-w-[260px]">
                          <div className="truncate" title={o.keyword}>{o.keyword}</div>
                          <div className="text-xs text-gray-400 font-normal truncate" title={o.reason}>{o.reason}</div>
                        </TableCell>
                        <TableCell><TypeBadge type={o.type} /></TableCell>
                        <TableCell className="text-right">{o.position}</TableCell>
                        <TableCell className="text-right">{fmtInt(o.impressions)}</TableCell>
                        <TableCell className="text-right">
                          {o.search_volume != null ? fmtInt(o.search_volume) : <span className="text-gray-300">—</span>}
                        </TableCell>
                        <TableCell className="text-right font-semibold text-klein">
                          +{fmtInt(o.potential_clicks)}
                        </TableCell>
                        <TableCell className="text-right pr-4">
                          <div className="flex items-center justify-end gap-2">
                            <div className="w-12 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                              <div className="h-full bg-klein" style={{ width: `${o.score}%` }} />
                            </div>
                            <span className="font-bold text-sm w-7 text-right">{o.score}</span>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
          <p className="text-xs text-gray-400 mt-3">
            "Potential" estimates additional monthly clicks if the keyword reaches its target position,
            using typical click-through rates by rank. Treat it as a relative guide, not a guarantee.
          </p>
        </>
      )}
    </div>
  );
}
